import * as Sentry from '@sentry/browser';
import { User } from '../types/auth';
import Formatter from '../utils/formatter';
import { convertLocale, getBaseCountry } from './helpers';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    analytics: any;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || '';
const VARIANT = process.env.NEXT_PUBLIC_BRANCH || process.env.BRANCH || 'local';
const ENVIRONMENT = process.env.NEXT_PUBLIC_ENV || 'development';

class Tracking {
  locale: string;

  constructor() {
    this.locale = 'en';
  }

  identify(user: User, referrerCode?: string) {
    const { ref, name, email, userType, phone, phoneCc } = user;

    Sentry.setUser({ id: ref, email });

    const data = {
      name: name || '',
      email,
      phone: phone && phoneCc ? Formatter.phone(phone, phoneCc) : '',
      region: getBaseCountry(),
      type: userType === 'business' ? 'business' : 'personal',
      // for hubspot contact
      user_type: userType === 'business' ? 'business' : 'personal',
      spaceship_pro_user: userType === 'business',
    } as Record<string, string | boolean>;

    // for hubspot contact
    if (referrerCode) data.referral_code = referrerCode;

    if (window.analytics) {
      window.analytics.identify(ref, data);
    }
  }

  reset() {
    Sentry.configureScope((scope) => scope.setUser(null));

    if (window.analytics) {
      window.analytics.reset();
    }
  }

  setLocale(locale: string) {
    this.locale = convertLocale(locale);
  }

  event(name: string, properties = {}, withLabel = false) {
    const opts = {
      ...properties,
      platform: 'web',
      environment: ENVIRONMENT,
      variant: VARIANT,
      version: APP_VERSION,
      device_locale: window?.navigator?.language || 'en',
      locale: this.locale || 'en',
    };

    if (window.analytics) {
      window.analytics.track(name, {
        // For GA events only
        label: withLabel ? JSON.stringify(opts) : undefined,
        ...opts,
      });
    }
  }

  page(name: string, properties = {}) {
    if (window.analytics) {
      window.analytics.page(name, {
        ...properties,
        platform: 'web',
        environment: ENVIRONMENT,
        variant: VARIANT,
        version: APP_VERSION,
        device_locale: window?.navigator?.language || 'en',
        locale: this.locale || 'en',
      });
    }
  }
}

export default new Tracking();
